import { createTheme } from "@mui/material/styles";

declare module "@mui/material/styles" {
  interface Theme {
    custom: {
      maxWidth: string;
      boxShadow: string;
      borderRadius: string;
      transition: string;
      gradients: {
        hero: string;
        heroGlow: string;
        cta: string;
        textAccent: string;
      };
      glass: {
        background: string;
        border: string;
        blur: string;
        backgroundLight: string;
        borderLight: string;
      };
    };
  }
  // Allow configuration using `createTheme`
  interface ThemeOptions {
    custom?: {
      maxWidth?: string;
      boxShadow?: string;
      borderRadius?: string;
      transition?: string;
      gradients?: {
        hero?: string;
        heroGlow?: string;
        cta?: string;
        textAccent?: string;
      };
      glass?: {
        background?: string;
        border?: string;
        blur?: string;
        backgroundLight?: string;
        borderLight?: string;
      };
    };
  }
}

const theme = createTheme({
  custom: {
    maxWidth: "1280px",
    boxShadow:
      "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)",
    borderRadius: "16px",
    transition: "all 0.3s ease-in-out",
    gradients: {
      hero: "linear-gradient(135deg, #0B1A2C 0%, #13293F 45%, #3A2417 100%)",
      heroGlow:
        "radial-gradient(ellipse 60% 50% at 70% 20%, rgba(199,93,44,0.35), transparent)",
      cta: "linear-gradient(120deg, #C75D2C 0%, #9E4520 100%)",
      textAccent: "linear-gradient(90deg, #DE9A5B, #C75D2C)",
    },
    glass: {
      background: "rgba(255,255,255,0.07)",
      border: "1px solid rgba(255,255,255,0.15)",
      blur: "blur(14px)",
      backgroundLight: "rgba(255,255,255,0.65)",
      borderLight: "1px solid rgba(19,41,63,0.08)",
    },
  },
  palette: {
    primary: {
      main: "#13293F",
      light: "#2E4A6B",
      dark: "#0B1A2C",
      contrastText: "#ffffff",
    },
    secondary: {
      main: "#C75D2C",
      light: "#DE9A5B",
      dark: "#9E4520",
      contrastText: "#ffffff",
    },
    background: {
      default: "#F7F5F2",
      paper: "#ffffff",
    },
    text: {
      primary: "#182430",
      secondary: "#5A6B7C",
    },
  },
  typography: {
    fontFamily:
      '"InterVariable", "Inter", -apple-system, "Segoe UI", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 800,
      fontSize: "clamp(2.4rem, 5vw, 3.6rem)",
      lineHeight: 1.1,
      letterSpacing: "-0.02em",
    },
    h2: {
      fontWeight: 700,
      fontSize: "clamp(1.9rem, 3.5vw, 2.5rem)",
      lineHeight: 1.2,
      letterSpacing: "-0.01em",
    },
    h3: {
      fontWeight: 650,
      fontSize: "1.35rem",
      lineHeight: 1.35,
    },
    h4: {
      fontWeight: 700,
      letterSpacing: "-0.01em",
    },
    overline: {
      fontWeight: 700,
      letterSpacing: "0.12em",
      fontSize: "0.8rem",
    },
    body1: {
      fontSize: "1rem",
      lineHeight: 1.65,
    },
    button: {
      textTransform: "none",
      fontWeight: 600,
    },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: "999px",
          padding: "10px 24px",
          fontWeight: 600,
        },
        contained: {
          boxShadow: "none",
          "&:hover": {
            boxShadow: "0 6px 20px rgba(19,41,63,0.18)",
            transform: "translateY(-1px)",
          },
          transition: "all 0.25s ease",
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: "999px",
          fontWeight: 600,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: "16px",
          boxShadow:
            "0 1px 3px 0 rgb(0 0 0 / 0.08), 0 1px 2px -1px rgb(0 0 0 / 0.08)",
          transition: "transform 0.25s ease-in-out, box-shadow 0.25s ease-in-out",
          "&:hover": {
            transform: "translateY(-4px)",
            boxShadow:
              "0 12px 24px -6px rgb(19 41 63 / 0.14), 0 4px 8px -4px rgb(19 41 63 / 0.1)",
          },
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          borderRadius: "12px",
          "&:before": { display: "none" },
        },
      },
    },
  },
});

export default theme;
